import cloneDeep from 'lodash/cloneDeep';
import floor from 'lodash/floor';
import React, { useRef } from 'react';
import { IFramework, useMoveEventListener } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { IImageLayerProps, ILayerProps, LayerType } from '@/version-types';
import { getMoveDistanceByMoveType, getMovePositionInImageCenter, isImageOutOfRangeLayer } from '../utils';

const ImageSizeChangeAction: React.FC<{
    stageScale: number;
    framework: IFramework;
}> = ({ stageScale, framework }) => {
    const currentLayerRef = useRef<ILayerProps | undefined>(undefined);

    useMoveEventListener((e: ITouchKitEvent) => {
        if (e.isStart) {
            const layer = framework.operate.getActivePickLayer();
            currentLayerRef.current = cloneDeep(layer as ILayerProps);
        }
        // 渲染层不存在，不可操作
        if (!currentLayerRef.current || currentLayerRef.current?.layerType !== LayerType.IMAGE_LAYER) {
            return null;
        }

        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');

        if (!dataType?.includes('crop-img-scale')) return;

        const { width, height, data } = currentLayerRef.current;
        const { imageTransform } = data as IImageLayerProps;
        const { offsetX, offsetY, widthRatio, heightRatio, scale: imageScale } = imageTransform;

        // 以缩放点的对向点为原点
        // 获取位移的x,y相对于坐标原点的位移距离和新的宽高
        // 将位移的距离转为图层坐标系
        // 计算offsetX offsetY 和宽高
        const movePositionInImageCorner = getMovePositionInImageCenter(
            currentLayerRef.current,
            {
                x: e.deltaX,
                y: e.deltaY,
            },
            stageScale,
        );

        const { newSizes, movePoints } = getMoveDistanceByMoveType(
            currentLayerRef.current,
            dataType,
            movePositionInImageCorner,
        );

        for (let i = newSizes.length - 1; i >= 0; i--) {
            const newSize = newSizes[i];
            const movePoint = movePoints[i];
            const newLayer = cloneDeep(currentLayerRef.current) as ILayerProps;
            const newImageScale = (newSize.width / (widthRatio * width * imageScale)) * imageScale;
            (newLayer.data as IImageLayerProps).imageTransform.scale = newImageScale;
            // 需要用新的宽度计算offsetX和offsetY
            (newLayer.data as IImageLayerProps).imageTransform.offsetX =
                (offsetX * widthRatio * width * imageScale + movePoint.x) / (widthRatio * width * newImageScale);
            (newLayer.data as IImageLayerProps).imageTransform.offsetY =
                (offsetY * heightRatio * height * imageScale + movePoint.y) / (heightRatio * height * newImageScale);

            const isOut = isImageOutOfRangeLayer(newLayer);

            if (!isOut) {
                Object.assign(imageTransform, (newLayer.data as IImageLayerProps).imageTransform);

                break;
            }
        }

        imageTransform.offsetX = floor(imageTransform.offsetX, 8);
        imageTransform.offsetY = floor(imageTransform.offsetY, 8);
        const resultLayer = cloneDeep(currentLayerRef.current);
        framework.stage.addOrUpdate(cloneDeep(resultLayer));
    });
    return null;
};

export default ImageSizeChangeAction;
